import { type FC, useState } from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { resetObitScrapeCache } from "@/shared/utils/obitScrapeCache";
import { EventType, trackEvent } from "@/shared/utils/core";
import { getOnboardingFlow } from "@/shared/utils/funnelAnalytics";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

export const ObituaryChoice: FC<FlowCustomComponentProps> = ({
  onComplete,
  setAnswer,
  answers,
}) => {
  const [obitUrl, setObitUrl] = useState((answers["obit-link"] as string) || "");
  const [urlError, setUrlError] = useState<string | null>(null);

  const isValidUrl = (value: string) => {
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleObituarySubmit = () => {
    if (!isValidUrl(obitUrl)) {
      setUrlError("Enter the full link, starting with https://");
      return;
    }
    resetObitScrapeCache();
    trackEvent(EventType.DECEDENT_PATH_SELECTED, {
      flow: getOnboardingFlow(answers),
      decedent_path: "obituary",
      selection_source: "user",
    });
    setAnswer("has-obituary", "yes");
    setAnswer("obit-link", obitUrl.trim());
    onComplete();
  };

  const handleManualPath = () => {
    trackEvent(EventType.DECEDENT_PATH_SELECTED, {
      flow: getOnboardingFlow(answers),
      decedent_path: "manual",
      selection_source: "user",
    });
    setAnswer("has-obituary", "no");
    onComplete();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleObituarySubmit();
    }
  };

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="obit-link">Obituary link</Label>
        <Input
          id="obit-link"
          type="text"
          placeholder="https://funeralhome.com/obituary"
          value={obitUrl}
          onChange={(e) => {
            setObitUrl(e.target.value);
            setUrlError(null);
          }}
          onKeyDown={handleKeyDown}
          className={urlError ? "border-destructive" : ""}
        />
        <FieldError message={urlError} />
      </div>
      <div className="flex flex-col items-center gap-2">
        <Button variant="brand" rounded className="w-full" onClick={handleObituarySubmit}>
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" onClick={handleManualPath}>
          I'll type it myself
        </Button>
      </div>
    </div>
  );
};
